import { Extension, Node, mergeAttributes } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { ManagedDocumentContent } from '@cdf/api-client-react';
import { assertDocumentSectionWrite } from '@cdf/api-zod';
import { isLiveDocumentTransaction } from './document-live-editing';

export const sectionPresentationKey = new PluginKey<boolean>('documentSectionPresentation');
export const DocumentSectionBoundary = Node.create({
  name: 'sectionBoundary', group: 'block', atom: true, selectable: false,
  addAttributes: () => ({ sectionId: { default: null, parseHTML: element => element.getAttribute('data-section-id') } }),
  parseHTML: () => [{ tag: 'div[data-section-boundary]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, { 'data-section-boundary': '', class: 'hidden', 'aria-hidden': 'true' })]
});
export const DocumentSectionIdentity = Extension.create({
  name: 'documentSectionIdentity',
  addGlobalAttributes: () => [{ types: ['heading'], attributes: {
    sectionId: { default: null, parseHTML: element => element.getAttribute('data-section-id'), renderHTML: attributes => attributes.sectionId ? { 'data-section-id': attributes.sectionId } : {} }
  } }, { types: ['heading','paragraph'], attributes: {
    sectionTitleId: { default: null, parseHTML: element => element.getAttribute('data-section-title-id'), renderHTML: attributes => attributes.sectionTitleId ? { 'data-section-title-id': attributes.sectionTitleId } : {} }
  } }]
});
export const DocumentSectionGuard = Extension.create<{ current: () => ManagedDocumentContent; fragment: boolean; independent: boolean }>({
  name: 'documentSectionGuard',
  addOptions: () => ({ current: () => ({ type: 'doc', content: [] }), fragment: false, independent: false }),
  addProseMirrorPlugins() {
    const { current, fragment, independent } = this.options;
    const editor = this.editor;
    return [new Plugin({
      key: sectionPresentationKey,
      state: { init: () => true, apply: (tr, value) => typeof tr.getMeta(sectionPresentationKey) === 'boolean' ? tr.getMeta(sectionPresentationKey) as boolean : value },
      filterTransaction(transaction, state) {
        if (!transaction.docChanged || isLiveDocumentTransaction(transaction) || transaction.getMeta('documentReplacement')) return true;
        if (fragment && transaction.doc.toJSON().content?.some((n: { type: string; attrs?: { level?: number; sectionId?: string; sectionTitleId?: string } }) => n.type === 'sectionBoundary' || n.attrs?.sectionTitleId || n.attrs?.sectionId || !independent && n.type === 'heading' && n.attrs?.level === 2)) return false;
        if (!current().sections) return true;
        try {
          assertDocumentSectionWrite({ ...current(), content: state.doc.toJSON().content }, { ...current(), content: transaction.doc.toJSON().content });
          return true;
        } catch { return false; }
      },
      props: {
        decorations(state) {
          const sections = current().sections;
          if (!sections) return DecorationSet.empty;
          let section: NonNullable<typeof sections>['items'][number] | undefined;
          const decorations: Decoration[] = [];
          state.doc.forEach((node, position) => {
            if (node.attrs.sectionId) {
              section = sections.items.find(s => s.id === node.attrs.sectionId);
              const item = section;
              if (item && !item.hidden && sectionPresentationKey.getState(state) !== false) decorations.push(Decoration.widget(position, view => {
                const marker = document.createElement(editor.isEditable ? 'button' : 'span');
                marker.textContent = 'Section · ' + item.title + ' · ' + item.protection.replace('_', ' ');
                marker.className = 'my-2 block w-full border-y border-dashed px-2 py-1 text-left text-xs text-muted-foreground';
                marker.contentEditable = 'false'; marker.dataset.sectionMarker = item.id;
                marker.setAttribute('aria-label', 'Section settings: ' + item.title);
                if (marker instanceof HTMLButtonElement) {
                  marker.type = 'button';
                  marker.addEventListener('mousedown', event => { event.preventDefault(); event.stopPropagation(); });
                  marker.addEventListener('click', () => view.dom.dispatchEvent(new CustomEvent('document-section-settings', { detail: { sectionId: item.id, trigger: marker } })));
                }
                return marker;
              }, { side: -1, key: item.id + ':' + item.title + ':' + item.protection + ':' + editor.isEditable }));
            }
            if (node.type.name === 'sectionBoundary' || node.attrs.sectionTitleId || section?.hidden) decorations.push(Decoration.node(position, position + node.nodeSize, { class: 'hidden', contenteditable: 'false', 'aria-hidden': 'true' }));
            else if (section && section.protection !== 'editable') decorations.push(Decoration.node(position, position + node.nodeSize, {
              class: 'bg-muted/40 border-l-2 border-foreground/40 pl-2', contenteditable: 'false', 'data-section-protection': section.protection
            }));
          });
          return DecorationSet.create(state.doc, decorations);
        }
      }
    })];
  }
});
